import { Action, createReducer, on } from '@ngrx/store';

import { UserProfileActions } from './user-profile.actions';
import { UserProfileState } from './user-profile.state';
import { Theme } from '../../features/shared/models/common/themes';
import { measureReducer } from '../../features/shared/utils/runtime-reducer-stats';
import { AppActions } from '../app/app.actions';

export const initialState: UserProfileState = {
  loading: false,
  columns: {},
};

const featureReducer = measureReducer(
  createReducer(
    initialState,
    on(
      UserProfileActions.LoadProfile,
      (state): UserProfileState => ({
        ...state,
        loading: true,
        error: undefined,
      })
    ),
    on(
      AppActions.LoadInitDataSuccess,
      (state, action): UserProfileState => ({
        ...state,
        columns: action.data?.assetSettings?.columns || {},
        datapointCompareSettings: action.data?.userSettings?.datapointCompareSettings,
        unlockedEntities: action.data?.userSettings?.unlockedEntities,
        completedTourIds: action.data?.userSettings?.toursCompleted,
        positionTableHiddenColumns: action.data?.userSettings?.positionTableHiddenColumns,
        positionFilters: action.data?.userSettings?.positionFilters,
        transactionTaskSettings: action.data?.userSettings?.transactionTaskSettings,
        favoriteDatapointFieldIds: action.data?.userSettings?.favoriteDatapointFieldIds || [],
      })
    ),
    on(
      UserProfileActions.SetDatapointCompareSettingsSuccess,
      (state, action): UserProfileState => ({
        ...state,
        datapointCompareSettings: action.datapointCompareSettings,
      })
    ),
    on(
      UserProfileActions.UpdateProfile,
      (state): UserProfileState => ({
        ...state,
        loading: true,
        error: undefined,
      })
    ),
    on(
      UserProfileActions.LoadProfileSuccess,
      UserProfileActions.UpdateProfileSuccess,
      (state, action): UserProfileState => ({
        ...state,
        loading: false,
        error: undefined,
        profile: action.profile,
        theme: action.profile?.theme,
        useNextTheme: action.profile?.theme === Theme.Next,
      })
    ),

    on(
      UserProfileActions.UpdateAssetSetting,
      (state, action): UserProfileState => ({
        ...state,
        columns: {
          ...state.columns,
          ...(action.setting.columns || {}),
        },
      })
    ),

    on(UserProfileActions.ResetPassword, (state): UserProfileState => ({ ...state, loading: true, error: undefined })),
    on(
      UserProfileActions.ResetPasswordSuccess,
      (state): UserProfileState => ({
        ...state,
        loading: false,
        error: undefined,
        passwordResetInitiated: true,
      })
    ),
    on(UserProfileActions.SetColumns, (state, action): UserProfileState => ({ ...state, columns: action.columns })),
    on(
      UserProfileActions.UnlockEntitySuccess,
      (state, action): UserProfileState => ({ ...state, unlockedEntities: action.unlockedEntities })
    ),
    on(
      UserProfileActions.ToggleCompletedToursSuccess,
      (state, action): UserProfileState => ({ ...state, completedTourIds: action.tourIds })
    ),
    on(
      UserProfileActions.UpdatePositionTableHiddenColumnsSuccess,
      (state, action): UserProfileState => ({
        ...state,
        positionTableHiddenColumns: action.columns,
      })
    ),
    on(
      UserProfileActions.UpdatePositionFiltersSuccess,
      (state, action): UserProfileState => ({
        ...state,
        positionFilters: action.filters,
      })
    ),
    on(
      UserProfileActions.UpdateTransactionTaskSettingsSuccess,
      (state, action): UserProfileState => ({
        ...state,
        transactionTaskSettings: action.settings,
      })
    ),
    on(
      UserProfileActions.SetFavoriteDatapointFieldIdsSuccess,
      (state, action): UserProfileState => ({
        ...state,
        favoriteDatapointFieldIds: action.favoriteDatapointFieldIds,
      })
    ),
    on(
      UserProfileActions.LoadProfileFail,
      UserProfileActions.UpdateProfileFail,
      UserProfileActions.ResetPasswordFail,
      UserProfileActions.SetDatapointCompareSettingsFail,
      (state, action): UserProfileState => ({
        ...state,
        loading: false,
        profile: undefined,
        theme: Theme.Light,
        useNextTheme: false,
        error: action.error,
      })
    )
  )
);

export function reducer(state: UserProfileState | undefined, action: Action) {
  return featureReducer(state, action);
}
